import React, { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Trash2 } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import type { InventoryEntityType } from '#types/types';

type UiLanguage = 'en' | 'fr';

interface EntityDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: InventoryEntityType;
  entityId: string;
  stringToTypeToConfirm: string;
  entityLabel?: string | null;
  onSubmit: (entityType: InventoryEntityType, entityId: string) => Promise<void>;
}

const ENTITY_LABELS: Record<UiLanguage, Partial<Record<InventoryEntityType, string>>> = {
  en: {
    Property: 'asset',
    Brand: 'brand',
    Portfolio: 'portfolio',
    Unit: 'unit',
    Floor: 'floor',
    Deal: 'deal',
    Lead: 'lead',
    Organisation: 'organisation',
    User: 'user',
    Project: 'project',
  },
  fr: {
    Property: 'actif',
    Brand: 'marque',
    Portfolio: 'portefeuille',
    Unit: 'unite',
    Floor: 'etage',
    Deal: 'deal',
    Lead: 'lead',
    Organisation: 'organisation',
    User: 'utilisateur',
    Project: 'projet',
  },
};

const COPY = {
  en: {
    title: 'Confirm entity deletion',
    dangerPrefix: 'This action is',
    dangerWord: 'irreversible',
    dangerSuffix: 'and will permanently delete this',
    confirmLegend: 'Confirm action',
    confirmLabel: (value: string) => `Type ${value} to confirm:`,
    cancel: 'Cancel',
    submit: 'Delete permanently',
    deleting: 'Deleting...',
    mismatch: 'Confirmation text does not match.',
  },
  fr: {
    title: "Confirmer la suppression d'entite",
    dangerPrefix: 'Cette action est',
    dangerWord: 'irreversible',
    dangerSuffix: 'et supprimera definitivement',
    confirmLegend: "Confirmation de l'action",
    confirmLabel: (value: string) => `Tapez ${value} pour confirmer :`,
    cancel: 'Annuler',
    submit: 'Supprimer definitivement',
    deleting: 'Suppression...',
    mismatch: 'Le texte de confirmation ne correspond pas.',
  },
} as const;

export const EntityDeletionModal = React.memo(({
  isOpen,
  onClose,
  entityType,
  entityId,
  stringToTypeToConfirm,
  entityLabel,
  onSubmit,
}: EntityDeletionModalProps) => {
  const language: UiLanguage = useKernelStore((state) => state.language);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = COPY[language];

  const normalizedExpected = useMemo(
    () => (stringToTypeToConfirm ?? '').trim(),
    [stringToTypeToConfirm]
  );

  const isConfirmationValid = confirmationInput.trim() === normalizedExpected;

  const entityKindLabel = useMemo(
    () => ENTITY_LABELS[language][entityType] ?? String(entityType).toLowerCase(),
    [entityType, language]
  );

  const submitLabel = useMemo(
    () => entityLabel?.trim() || normalizedExpected || `${entityKindLabel} ${entityId}`,
    [entityId, entityKindLabel, entityLabel, normalizedExpected]
  );

  const handleConfirmationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setConfirmationInput(event.target.value);
  }, []);

  const handleSubmit = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    if (!isConfirmationValid) {
      setError(t.mismatch);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit(entityType, entityId);
    } catch (submitError: any) {
      setError(submitError?.message || 'Deletion failed.');
    } finally {
      setIsSubmitting(false);
    }
  }, [entityId, entityType, isConfirmationValid, onSubmit, t.mismatch]);

  useEffect(() => {
    if (!isOpen) return;
    setConfirmationInput('');
    setIsSubmitting(false);
    setError(null);
  }, [isOpen, entityId, entityType, normalizedExpected]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-lg border border-neutral-700 bg-neutral-800 p-6 shadow-xl">
        <div className="mb-4 flex items-center">
          <AlertTriangle size={24} className="mr-3 flex-shrink-0 text-red-500" />
          <h2 className="text-xl font-semibold text-white">{t.title}</h2>
        </div>

        <p className="mb-4 text-sm text-neutral-300">
          {t.dangerPrefix} <strong className="text-red-400">{t.dangerWord}</strong> {t.dangerSuffix}{' '}
          <strong className="text-yellow-400">{entityKindLabel}</strong>:{' '}
          <strong className="text-yellow-400">&quot;{submitLabel}&quot;</strong> (ID:{' '}
          <code className="rounded bg-neutral-700 px-1 text-xs">{entityId}</code>)
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset className="rounded-md border border-yellow-600 p-4">
            <legend className="px-1 text-sm font-medium text-yellow-400">{t.confirmLegend}</legend>
            <label htmlFor="entity-delete-confirm-input" className="mb-1 block text-xs font-medium text-neutral-300">
              {t.confirmLabel(`"${normalizedExpected}"`)}
            </label>
            <input
              id="entity-delete-confirm-input"
              type="text"
              value={confirmationInput}
              onChange={handleConfirmationChange}
              autoComplete="off"
              required
              disabled={isSubmitting}
              className={`w-full rounded-md border bg-neutral-700 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 ${
                isConfirmationValid ? 'border-green-500 focus:ring-green-500' : 'border-red-600 focus:ring-red-500'
              }`}
            />
          </fieldset>

          {error && <p className="pt-1 text-sm text-red-400">{error}</p>}

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md bg-neutral-600 px-4 py-2 text-sm text-white transition duration-150 hover:bg-neutral-500 disabled:opacity-50"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={!isConfirmationValid || isSubmitting}
              className="flex w-44 items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm text-white transition duration-150 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="mr-1.5 -ml-1 animate-spin" /> {t.deleting}
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-1.5 -ml-1" /> {t.submit}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

EntityDeletionModal.displayName = 'EntityDeletionModal';


